import { c as _c } from "react/compiler-runtime";
import { MouseEventHandler, ReactElement, ReactNode, memo } from 'react';
import { ButtonType } from '../types/ButtonType';
import ImageComponent from '@spa/components/image-component/image.component';
import classNames from 'clsx';
import styles from './button.component.module.scss';
export enum IconSize {
  EXTRA_SMALL,
  SMALL,
  MEDIUM,
  LARGE,
}

/**
 * Valid, non-deprecated `aria-haspopup` values per WAI-ARIA 1.2. The legacy boolean form
 * (`aria-haspopup="true"`) is deprecated as ambiguous, so consumers must name the kind of
 * popup the button actually opens; omitting the prop omits the attribute entirely.
 */
export type AriaPopupKind = 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog';
export interface Icon {
  readonly width: number;
  readonly height: number;
  readonly src: string;
  readonly alt: string;
  readonly unoptimized?: boolean;
}
export interface ButtonProps {
  /** Button style Primary/Secondary/As Text */
  readonly buttonType: ButtonType;
  /** Button left Icon */
  readonly iconLeft?: Icon;
  /** Button right icon */
  readonly iconRight?: Icon;
  /** Icon size */
  readonly iconSize?: IconSize;
  /** Show a spinner */
  readonly showSpinner?: boolean;
  /** Button content */
  readonly text?: ReactNode;
  /** Content in capital letters */
  readonly uppercase?: boolean;
  /** text underline */
  readonly underline?: boolean;
  /** Text bold */
  readonly bold?: boolean;
  /** onClick function */
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Disable button */
  readonly disabled?: boolean;
  /** Disable button */
  readonly looksDisabled?: boolean;
  /** Button type */
  readonly type?: 'button' | 'submit';
  /** The kind of popup the button opens; emits a valid `aria-haspopup` enum value (omitted when unset) */
  readonly popupKind?: AriaPopupKind;
  /** Indicates if the button represents a toggled-on/selected state (sets aria-pressed) */
  readonly pressed?: boolean;
  /*** Remove button styles */
  readonly noStyle?: boolean;
  readonly dataTrackId?: string;
  readonly dataTestId?: string;
  readonly dataProductId?: string;
}
function ButtonComponent(t0) {
  const $ = _c(48);
  const {
    buttonType,
    disabled,
    looksDisabled,
    iconLeft,
    showSpinner,
    text,
    uppercase: t1,
    underline: t2,
    bold: t3,
    iconRight,
    type,
    onClick,
    iconSize: t4,
    popupKind,
    pressed,
    noStyle,
    dataTrackId,
    dataTestId,
    dataProductId
  } = t0;
  const uppercase = t1 === undefined ? false : t1;
  const underline = t2 === undefined ? true : t2;
  const bold = t3 === undefined ? false : t3;
  const iconSize = t4 === undefined ? IconSize.MEDIUM : t4;
  const t5 = buttonType === ButtonType.PRIMARY;
  const t6 = buttonType === ButtonType.SECONDARY;
  const t7 = buttonType === ButtonType.TERTIARY;
  const t8 = buttonType === ButtonType.OUTLINE;
  const t9 = buttonType === ButtonType.AS_TEXT;
  const t10 = underline && buttonType === ButtonType.AS_TEXT;
  const t11 = text === undefined;
  let t12;
  if ($[0] !== bold || $[1] !== disabled || $[2] !== looksDisabled || $[3] !== t10 || $[4] !== t11 || $[5] !== t5 || $[6] !== t6 || $[7] !== t7 || $[8] !== t8 || $[9] !== t9 || $[10] !== uppercase) {
    t12 = classNames({
      [styles.button]: true,
      [styles["button--primary"]]: t5,
      [styles["button--secondary"]]: t6,
      [styles["button--tertiary"]]: t7,
      [styles["button--outline"]]: t8,
      [styles["button--as-text"]]: t9,
      [styles["button--underline"]]: t10,
      [styles["button--uppercase"]]: uppercase,
      "bold": bold,
      [styles["button--disabled"]]: disabled,
      [styles["button--looks-disabled"]]: looksDisabled,
      [styles["button--icon-only"]]: t11
    });
    $[0] = bold;
    $[1] = disabled;
    $[2] = looksDisabled;
    $[3] = t10;
    $[4] = t11;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
    $[10] = uppercase;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const buttonClass = t12;
  const t13 = iconSize === IconSize.SMALL;
  const t14 = iconSize === IconSize.MEDIUM;
  const t15 = iconSize === IconSize.LARGE;
  let t16;
  if ($[12] !== t13 || $[13] !== t14 || $[14] !== t15) {
    t16 = classNames({
      [styles["button-icon--small"]]: t13,
      [styles["button-icon--medium"]]: t14,
      [styles["button-icon--large"]]: t15
    });
    $[12] = t13;
    $[13] = t14;
    $[14] = t15;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  const iconClass = t16;
  let t17;
  if ($[16] !== iconSize) {
    t17 = function getIconSize() {
      let size;
      bb22: switch (iconSize) {
        case IconSize.LARGE:
          {
            size = 28;
            break bb22;
          }
        case IconSize.MEDIUM:
          {
            size = 16;
            break bb22;
          }
        case IconSize.SMALL:
          {
            size = 12;
            break bb22;
          }
        case IconSize.EXTRA_SMALL:
          {
            size = 8;
          }
      }
      return `${size}px`;
    };
    $[16] = iconSize;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  const getIconSize = t17;
  let t18;
  if ($[18] !== getIconSize || $[19] !== iconClass || $[20] !== iconLeft) {
    t18 = iconLeft === undefined ? null : <ImageComponent src={iconLeft.src} alt={iconLeft.alt} width={iconLeft.width} height={iconLeft.height} className={iconClass} sizes={getIconSize()} unoptimized={iconLeft.unoptimized ?? false} />;
    $[18] = getIconSize;
    $[19] = iconClass;
    $[20] = iconLeft;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  const leftIconImage = t18;
  let t19;
  if ($[22] !== getIconSize || $[23] !== iconClass || $[24] !== iconRight) {
    t19 = iconRight === undefined ? null : <ImageComponent src={iconRight.src} alt={iconRight.alt} width={iconRight.width} height={iconRight.height} className={iconClass} sizes={getIconSize()} unoptimized={iconRight.unoptimized} />;
    $[22] = getIconSize;
    $[23] = iconClass;
    $[24] = iconRight;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  const rightIconImage = t19;
  let t20;
  if ($[26] !== iconLeft || $[27] !== iconRight || $[28] !== leftIconImage || $[29] !== rightIconImage || $[30] !== showSpinner || $[31] !== text) {
    t20 = showSpinner ? <ImageComponent key="spinner" className={styles["button-icon--spinning"]} src="/assets/icons/spinner-icon.svg" alt="spinner-icon" width={80} height={80} /> : <span className={classNames({
      [styles.button__content]: true,
      [styles["button__content--has-icons"]]: iconLeft != null && iconRight != null
    })}>{leftIconImage}{text}{rightIconImage}</span>;
    $[26] = iconLeft;
    $[27] = iconRight;
    $[28] = leftIconImage;
    $[29] = rightIconImage;
    $[30] = showSpinner;
    $[31] = text;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const content = t20;
  let t21;
  if ($[33] !== disabled || $[34] !== onClick) {
    t21 = event => {
      if (onClick && !disabled) {
        event.preventDefault();
        onClick(event);
      }
    };
    $[33] = disabled;
    $[34] = onClick;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  const onClickOverride = t21;
  const t22 = type ?? "button";
  const t23 = noStyle ? styles["button--no-style"] : buttonClass;
  const t24 = disabled ? -1 : 0;
  let t25;
  if ($[36] !== content || $[37] !== dataProductId || $[38] !== dataTestId || $[39] !== dataTrackId || $[40] !== disabled || $[41] !== onClickOverride || $[42] !== popupKind || $[43] !== pressed || $[44] !== t22 || $[45] !== t23 || $[46] !== t24) {
    t25 = <button type={t22} onClick={onClickOverride} className={t23} tabIndex={t24} aria-disabled={disabled} aria-haspopup={popupKind} aria-pressed={pressed} data-track-id={dataTrackId} data-testid={dataTestId} data-product-id={dataProductId}>{content}</button>;
    $[36] = content;
    $[37] = dataProductId;
    $[38] = dataTestId;
    $[39] = dataTrackId;
    $[40] = disabled;
    $[41] = onClickOverride;
    $[42] = popupKind;
    $[43] = pressed;
    $[44] = t22;
    $[45] = t23;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  return t25;
}
export default memo(ButtonComponent);